import { Box, Container, Paper, Typography, alpha, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * MetricsBand — the founder's portfolio-traction proof strip.
 *
 * Single source for the headline metrics, rendered on the home page
 * (new-visitor flow) and on /portfolio-kit. The attribution + "your numbers
 * will vary" caveat are REQUIRED (FTC substantiation — these figures are n=1,
 * the founder's own portfolio over three months). Do not remove the caveat.
 *
 * `sx` is merged onto the outer Container so callers can add page-specific
 * styling (e.g. PortfolioKitHome's fade-in). Defaults to none — static and
 * visible — so it is safe on pages without the fadeUp keyframes.
 */
export default function MetricsBand({ sx }: { sx?: SxProps<Theme> }) {
  const theme = useTheme();
  const { t } = useTranslation("home");
  return (
    <Container
      maxWidth="lg"
      sx={[{ py: { xs: 4, md: 6 } }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 3,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h2" fontWeight={500} sx={{ color: theme.palette.primary.main, lineHeight: 1 }}>
              {t("MetricsBand.metricLinkedinViews")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("MetricsBand.linkedinProfileViews")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h2" fontWeight={500} sx={{ color: theme.palette.primary.main, lineHeight: 1 }}>
              {t("MetricsBand.metricRecruiterSearches")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("MetricsBand.recruiterSearchAppearances")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h2" fontWeight={500} sx={{ color: theme.palette.primary.main, lineHeight: 1 }}>
              {t("MetricsBand.metricInterviews")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("MetricsBand.interviewsIn90Days")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h2" fontWeight={500} sx={{ color: theme.palette.primary.main, lineHeight: 1 }}>
              {t("MetricsBand.metricTimeframe")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("MetricsBand.timeToNumbers")}
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t("MetricsBand.caveat")}
        </Typography>
      </Paper>
    </Container>
  );
}
